import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DataTable } from '@/components/ui/data-table';
import type { Column } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchSelect } from '@/components/ui/search-select';
import type { SearchSelectOption } from '@/components/ui/search-select';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import {
  ArrowRightLeft,
  Eye,
  Database,
  Loader2,
  CheckCircle,
  AlertTriangle,
  School,
} from 'lucide-react';
import { migrationV1V2Api } from './api/migration-v1-v2';
import type { MigrationSchoolEntry, SchoolFilters } from './api/migration-v1-v2';
import { ProjectStatusBadge } from './components/ProjectStatusBadge';
import { AssignUserControl } from './components/AssignUserControl';
import { useAssignableUsers } from './hooks/useAssignableUsers';
import type { ProjectStatus } from './types';

const formatDate = (dateString: string | null) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const migrationStatusOptions = [
  { value: 'all', label: 'Toutes les migrations' },
  { value: 'not_started', label: 'Pas commencée' },
  { value: 'in_progress', label: 'En cours' },
  { value: 'completed', label: 'Terminée' },
  { value: 'failed', label: 'Échouée' },
];

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}

function StatCard({ title, value, icon: Icon, color }: StatCardProps) {
  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-gray-100 dark:border-slate-700 shadow-sm">
      <div className="p-4 flex items-center gap-3">
        <div className={cn('p-2.5 rounded-lg', color)}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
        </div>
      </div>
    </div>
  );
}

export function ProjectsListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, hasPermission } = useAuth();
  const canManage = hasPermission('action.migration_v1_v2.manage');
  const assignableUsers = useAssignableUsers(canManage);

  // Ouverture du dossier : admins (manage) partout ; sinon uniquement le
  // responsable assigné (une école sans projet nécessite manage, l'ouverture
  // créant le projet).
  const canOpenSchool = (school: MigrationSchoolEntry) =>
    canManage || (!!school.migrationProject && school.migrationProject.assignedTo?.id === user?.id);

  const [schools, setSchools] = useState<MigrationSchoolEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [stats, setStats] = useState({ totalSchools: 0, notStarted: 0, inProgress: 0, completed: 0, failed: 0 });
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [openingSchoolId, setOpeningSchoolId] = useState<number | null>(null);

  const filters = useMemo<SchoolFilters>(
    () => ({
      search: searchParams.get('search') || undefined,
      migrationStatus: searchParams.get('migrationStatus') || undefined,
      ownerId: searchParams.get('ownerId') ? Number(searchParams.get('ownerId')) : undefined,
      sortBy: searchParams.get('sortBy') || 'name',
      sortOrder: (searchParams.get('sortOrder') as 'ASC' | 'DESC') || 'ASC',
      page: Number(searchParams.get('page')) || 1,
      limit: Number(searchParams.get('limit')) || 25,
    }),
    [searchParams]
  );

  const updateFilters = useCallback(
    (newFilters: Partial<SchoolFilters>) => {
      const updated = { ...filters, ...newFilters };
      const params = new URLSearchParams();
      if (updated.search) params.set('search', updated.search);
      if (updated.migrationStatus) params.set('migrationStatus', updated.migrationStatus);
      if (updated.ownerId && updated.ownerId !== 'all') params.set('ownerId', String(updated.ownerId));
      if (updated.sortBy && updated.sortBy !== 'name') params.set('sortBy', updated.sortBy);
      if (updated.sortOrder && updated.sortOrder !== 'ASC') params.set('sortOrder', updated.sortOrder);
      if (updated.page && updated.page !== 1) params.set('page', updated.page.toString());
      if (updated.limit && updated.limit !== 25) params.set('limit', updated.limit.toString());
      setSearchParams(params);
    },
    [filters, setSearchParams]
  );

  const handleSort = useCallback(
    (key: string) => {
      const isSameField = filters.sortBy === key;
      const newOrder = isSameField && filters.sortOrder === 'ASC' ? 'DESC' : 'ASC';
      updateFilters({ sortBy: key, sortOrder: newOrder, page: 1 });
    },
    [filters.sortBy, filters.sortOrder, updateFilters]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== (filters.search || '')) {
        updateFilters({ search: searchInput || undefined, page: 1 });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, filters.search, updateFilters]);

  const fetchSchools = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await migrationV1V2Api.getSchools(filters);
      setSchools(response.data);
      setTotalItems(response.meta.total);
      setTotalPages(response.meta.totalPages);
      if (response.meta.stats) {
        setStats(response.meta.stats);
      }
    } catch {
      setError('Erreur lors du chargement des écoles');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchSchools();
  }, [fetchSchools]);

  const openSchool = async (school: MigrationSchoolEntry) => {
    if (school.migrationProject) {
      navigate(`/migration-v1-v2/${school.migrationProject.id}`);
      return;
    }
    try {
      setOpeningSchoolId(school.id);
      const project = await migrationV1V2Api.createProject({ schoolId: school.id });
      navigate(`/migration-v1-v2/${project.id}`);
    } catch {
      setError("Impossible d'ouvrir le projet de migration");
    } finally {
      setOpeningSchoolId(null);
    }
  };

  // stats come from backend meta.stats (global, not page-scoped)

  const ownerFilterOptions = useMemo<SearchSelectOption[]>(
    () => [
      { value: 'all', label: 'Tous responsables' },
      ...assignableUsers.map((u) => ({
        value: String(u.id),
        label: `${u.firstname} ${u.lastname}`.trim() || u.email || `#${u.id}`,
        searchText: u.email ?? undefined,
      })),
    ],
    [assignableUsers]
  );

  const columns: Column<MigrationSchoolEntry>[] = [
    {
      key: 'name',
      header: 'École',
      sortable: true,
      render: (school) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900 dark:text-gray-100">{school.name}</span>
          <Badge variant="outline" className="font-mono text-xs">{school.code}</Badge>
        </div>
      ),
    },
    {
      key: 'productionDate',
      header: 'Mise en prod',
      sortable: true,
      className: 'w-32',
      render: (school) => (
        <span className="text-sm text-muted-foreground">{formatDate(school.productionDate)}</span>
      ),
    },
    {
      key: 'migration',
      header: 'Migration',
      className: 'w-40',
      render: (school) => {
        if (!school.migrationProject) {
          return <span className="text-sm text-gray-400">Pas commencée</span>;
        }
        return <ProjectStatusBadge status={school.migrationProject.status as ProjectStatus} />;
      },
    },
    {
      key: 'assignedTo',
      header: 'Responsable',
      className: 'w-60',
      render: (school: MigrationSchoolEntry) => {
        const p = school.migrationProject;
        if (!p) return <span className="text-sm text-gray-400">—</span>;
        // Seul un admin (manage) peut changer le responsable ; les autres le
        // voient en lecture seule.
        if (!canManage) {
          return p.assignedTo ? (
            <span className="text-sm">
              {`${p.assignedTo.firstname ?? ''} ${p.assignedTo.lastname ?? ''}`.trim() || '—'}
            </span>
          ) : (
            <span className="text-sm text-gray-400">Non assigné</span>
          );
        }
        return (
          <AssignUserControl
            projectId={p.id}
            assignedTo={p.assignedTo}
            users={assignableUsers}
            onAssigned={(proj) =>
              setSchools((prev) =>
                prev.map((s) =>
                  s.id === school.id && s.migrationProject
                    ? {
                        ...s,
                        migrationProject: {
                          ...s.migrationProject,
                          assignedTo: proj.assignedTo,
                        },
                      }
                    : s
                )
              )
            }
            onError={(m) => setError(m)}
            triggerClassName="h-8 text-xs"
          />
        );
      },
    } as Column<MigrationSchoolEntry>,
    {
      key: 'progress',
      header: 'Progression',
      sortable: true,
      className: 'w-40',
      render: (school) => {
        const p = school.migrationProject;
        if (!p || p.totalRecords === 0) return <span className="text-gray-400">—</span>;
        const percent = Math.round((p.processedRecords / p.totalRecords) * 100);
        return (
          <div className="space-y-1">
            <Progress value={percent} />
            <span className="text-xs text-gray-500">{p.processedRecords}/{p.totalRecords}</span>
          </div>
        );
      },
    },
    {
      key: 'startedAt',
      header: 'Début',
      sortable: true,
      className: 'w-28',
      render: (school) => (
        <span className="text-sm text-muted-foreground">
          {school.migrationProject?.startedAt ? formatDate(school.migrationProject.startedAt) : '—'}
        </span>
      ),
    },
    {
      key: 'completedAt',
      header: 'Fin',
      sortable: true,
      className: 'w-28',
      render: (school) => (
        <span className="text-sm text-muted-foreground">
          {school.migrationProject?.completedAt ? formatDate(school.migrationProject.completedAt) : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right w-36',
      render: (school) => {
        const isOpening = openingSchoolId === school.id;
        if (!canOpenSchool(school)) {
          return null;
        }
        return (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openSchool(school)}
            disabled={isOpening}
          >
            {isOpening ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Eye className="h-4 w-4 mr-1" />
            )}
            Ouvrir
          </Button>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 text-white">
            <ArrowRightLeft className="h-5 w-5" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
            Migration V1 → V2
          </h1>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard title="Écoles éligibles" value={stats.totalSchools} icon={School} color="bg-blue-100 text-blue-600" />
        <StatCard title="Pas commencées" value={stats.notStarted} icon={Database} color="bg-gray-100 text-gray-600" />
        <StatCard title="En cours" value={stats.inProgress} icon={Loader2} color="bg-orange-100 text-orange-600" />
        <StatCard title="Terminées" value={stats.completed} icon={CheckCircle} color="bg-green-100 text-green-600" />
        <StatCard title="Échouées" value={stats.failed} icon={AlertTriangle} color="bg-red-100 text-red-600" />
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">{error}</div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <Select
          value={filters.migrationStatus || 'all'}
          onValueChange={(val) => updateFilters({ migrationStatus: val === 'all' ? undefined : val, page: 1 })}
        >
          <SelectTrigger className="w-52">
            <SelectValue placeholder="Toutes les migrations" />
          </SelectTrigger>
          <SelectContent>
            {migrationStatusOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {canManage && (
          <SearchSelect
            className="w-60"
            options={ownerFilterOptions}
            value={filters.ownerId ? String(filters.ownerId) : 'all'}
            onValueChange={(val) =>
              updateFilters({ ownerId: val === 'all' ? 'all' : Number(val), page: 1 })
            }
            placeholder="Tous responsables"
            searchPlaceholder="Rechercher un responsable…"
          />
        )}
      </div>

      {/* DataTable */}
      <DataTable
        columns={columns}
        data={schools}
        loading={loading}
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder="Rechercher par nom ou code d'école..."
        sortBy={filters.sortBy}
        sortOrder={filters.sortOrder}
        onSort={handleSort}
        page={filters.page}
        pageSize={filters.limit}
        totalItems={totalItems}
        totalPages={totalPages}
        onPageChange={(page) => updateFilters({ page })}
        onPageSizeChange={(limit) => updateFilters({ limit, page: 1 })}
        emptyMessage="Aucune école trouvée"
      />

    </div>
  );
}
